import React from 'react';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { getSafeImageUrl } from '../../utils/imageUtils';
import { OpenTvLoader } from '../common/OpenTvLoader';

export interface TvLoadingOverlayProps {
  title: string;
  image?: string;
  type: 'live' | 'vod';
  status?: string;
  subtitle?: string;
  bufferingSeconds?: number;
  onClose?: () => void;
  onNextChannel?: () => void;
  onPrevChannel?: () => void;
  onRetry?: () => void;
}

export const TvLoadingOverlay: React.FC<TvLoadingOverlayProps> = ({
  title,
  image,
  type,
  status = 'Sintonizando...',
  subtitle,
  bufferingSeconds,
  onNextChannel,
  onPrevChannel,
  onRetry,
}) => {
  const safeImg = image ? getSafeImageUrl(image) : null;
  // V8.5.1: igual ao Stick — no carregamento normal aparece só o logo e o nome (sem "Sintonizando..." e sem a
  // linha de baixo). A linha amarela só aparece quando há problema (servidor, sem sinal etc.); o texto explicativo
  // de baixo não aparece mais.
  const isNormalLoading = !status || /^(sintonizando|carregando|aguardando sinal)/i.test(status.trim());
  const shownStatus = isNormalLoading ? undefined : status;
  void subtitle;

  return (
    <div className="tv-loading-overlay fixed inset-0 z-20 flex flex-col items-center justify-center p-4 sm:p-6 select-none animate-in fade-in duration-200 pointer-events-none">
      {/* Fundo escuro com leve blur sem cobrir barras ou cliques externos */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm pointer-events-none" />

      {/* Caixa central com a tevezinha e textos */}
      <div className="relative z-10 flex flex-col items-center justify-center pointer-events-auto max-w-lg w-full px-2">
        <OpenTvLoader
          size="hero"
          image={safeImg || undefined}
          title={title}
          status={shownStatus}
          glow="none"
        />

        {/* Botões de atalho rápido para troca de canal se a conexão demorar */}
        {bufferingSeconds !== undefined && bufferingSeconds >= 6 && (onPrevChannel || onNextChannel || onRetry) && (
          <div className="mt-4 sm:mt-5 flex items-center justify-center gap-2 sm:gap-3 flex-wrap animate-in fade-in slide-in-from-bottom-2 duration-300">
            {onPrevChannel && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onPrevChannel();
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl bg-zinc-800/90 hover:bg-zinc-700 text-zinc-200 hover:text-white border border-zinc-700/60 font-medium text-xs sm:text-sm transition active:scale-95 cursor-pointer shadow-lg"
                title="Canal Anterior"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>Anterior</span>
              </button>
            )}

            {onRetry && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry();
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl bg-theme-accent hover:opacity-90 text-black font-bold text-xs sm:text-sm transition active:scale-95 cursor-pointer shadow-lg shadow-theme-accent/20"
                title="Tentar Novamente"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Reconectar</span>
              </button>
            )}

            {onNextChannel && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onNextChannel();
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl bg-zinc-800/90 hover:bg-zinc-700 text-zinc-200 hover:text-white border border-zinc-700/60 font-medium text-xs sm:text-sm transition active:scale-95 cursor-pointer shadow-lg"
                title="Próximo Canal"
              >
                <span>Próximo</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


